import CategorySwiper from '@components/category-swiper/CategorySwiper';
import {
    CategoriesFragment,
    ContentBlockFragment,
    DiscoverMainContentFolderFragment,
} from '@graphqlTypes/sitecore';
import { useRouter } from 'next/router';
import { FreeMode, Grid } from 'swiper';
import { useTranslation } from 'next-i18next';
import {
    blockTypeLinkTo,
    contentArr,
    findCategoryTitleByKey,
    isDeepLinkShareUrl,
    isShareUrl,
    isTimeFrame,
    isVideoPop,
    objFilter,
    removeCIDUrl,
    videoNative,
} from '@src/utils/util';
import { withBasePath } from '@src/common/path-convert';
import { jsBridge } from '@src/utils/js-bridge';
import { useGlobalStore } from '@src/hooks/use-global-store';
import ImgLazy from '@components/img-lazy';
import isSharePageStore from '@mobx/stores/isShare-store';
import cn from 'classnames';
import styles from './RowOfTwo.module.scss';

type Props = {
    content: DiscoverMainContentFolderFragment;
    filterValue: string;
    categoryData: CategoriesFragment[];
};

export default function RowOfTwo({ content, filterValue, categoryData }: Props) {
    // console.log('leisure------', content);
    const router = useRouter();
    const { t } = useTranslation();
    const newArr = contentArr(filterValue, content);
    const { settingStore } = useGlobalStore();
    const videoPopupStore = useGlobalStore().videoPopupStore;
    const isSharePage = isSharePageStore.isSharePage;
    const isZHCN = content?.useSimplifiedChinese?.boolValue ?? false;

    return (
        <div className='py-[12px] row-of-two'>
            <div className='flex justify-between mb-[12px] px-[12px] items-baseline sm:pl-[24px]'>
                <div
                    className='text-[20px] max-w-[80%]'
                    dangerouslySetInnerHTML={{
                        __html: content?.sectionTitle?.value || '',
                    }}
                ></div>
                {newArr.length > 1 && (
                    <div
                        className='text-[13px] text-[#122C68]'
                        onClick={() => {
                            if (isSharePage) {
                                return;
                            }
                            router.push(removeCIDUrl(`${content.key}${location.search}`));
                        }}
                    >
                        {isZHCN ? '更多' : t('common:seeAll')}
                    </div>
                )}
            </div>
            <CategorySwiper
                options={{
                    grid: {
                        rows: 2,
                        fill: 'row',
                    },
                    modules: [Grid, FreeMode],
                    resizeObserver: true,
                    slidesPerView: 'auto',
                }}
                spaceBetween={8}
                slideClassName='rounded overflow-hidden !mt-0 !w-[332px]'
                swiperClassName={`px-[15px] sm:px-[24px] ${styles.rowOfTwoSwiper}`}
            >
                {newArr.map((i: ContentBlockFragment) => {
                    const isDeepLink = i.blockType?.value === 'DeepLink';
                    const categoryTitle =
                        (i?.category?.categories &&
                            i.category.categories.length > 0 &&
                            objFilter(i?.category?.categories)[0]?.categoryTitle?.value) ||
                        '';
                    const categoryName =
                        (i?.category?.categories &&
                            i.category.categories.length > 0 &&
                            objFilter(i?.category?.categories)[0]?.name) ||
                        '';
                    const categoryText = isZHCN
                        ? findCategoryTitleByKey(categoryName, categoryData)
                        : categoryTitle;

                    return (
                        <div key={i.key} className={cn(isSharePage && 'pointer-events-none')}>
                            {isDeepLink ? (
                                <div className='relative'>
                                    {i.shareIcon?.boolValue && (
                                        <img
                                            loading='lazy'
                                            className='w-[20px] absolute top-[8px] right-[8px] z-10'
                                            src={withBasePath('/images/share_blue.svg')}
                                            alt=''
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                if (isDeepLink) {
                                                    return jsBridge.appShareUrl(
                                                        isDeepLinkShareUrl(i.deepLink?.value ?? ''),
                                                        i
                                                    );
                                                }

                                                const shareLink =
                                                    i.blockType?.value === 'Internal'
                                                        ? i.internalLink?.value ?? ''
                                                        : `${content?.key}/${i.key}/`;

                                                jsBridge.appShareUrl(
                                                    `${isShareUrl(
                                                        i.blockType?.value,
                                                        shareLink,
                                                        i.raceLink?.targetItem?.path
                                                    )}`,
                                                    i
                                                );
                                            }}
                                        />
                                    )}
                                    <a href={i.deepLink?.value ?? ''} target='_blank'>
                                        <div
                                            className={`relative bg-white p-[10px] mb-[10px] flex rounded-[4px] h-[120px]`}
                                            key={i.key}
                                        >
                                            <div className='w-[100px] h-[100px] rounded-[4px] object-cover'>
                                                <ImgLazy
                                                    classNames='rounded-[4px]'
                                                    width={100}
                                                    height={100}
                                                    src={i?.heroImage?.src ?? ''}
                                                />
                                            </div>

                                            {isTimeFrame(
                                                (i.isIconDisplay && i.isIconDisplay.boolValue) ||
                                                    false,
                                                i.iconDisplayStartDate?.dateValue || 0,
                                                i.iconDisplayEndDate?.dateValue || 0
                                            ) && (
                                                <img
                                                    src={i.iconDisplay?.targetItem?.iconImage?.src}
                                                    alt=''
                                                    loading='lazy'
                                                    className='h-[20px] absolute top-[12px] left-[12px]'
                                                ></img>
                                            )}
                                            <div className='pl-[10px] flex flex-col justify-start break-all'>
                                                <div
                                                    className='text-[#031E6C] text-[13px] mb-[2px] categoryBr min-h-[16.5px] line-clamp-1'
                                                    dangerouslySetInnerHTML={{
                                                        __html: categoryText,
                                                    }}
                                                ></div>
                                                <div
                                                    className='text-[15px] line-clamp-4 leading-[1.27] min-h-[78px] align-bottom'
                                                    style={{ wordBreak: 'break-word' }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: i.pageTitle?.value || '',
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            ) : (
                                <div
                                    className={`relative bg-white p-[10px] mb-[10px] flex rounded-[4px] h-[120px] ${isVideoPop(
                                        i
                                    )}`}
                                    key={i.key}
                                    data-poster={i.videoLink?.value}
                                    data-image={i.videoPoster?.src}
                                    onClick={() => {
                                        videoNative(i, jsBridge);

                                        if (i.blockType?.value === 'Video') {
                                            return videoPopupStore.updateContentBlockData(i);
                                        }

                                        const generalLink = removeCIDUrl(
                                            `/${content.key}/${i.key}${location.search}`
                                        );

                                        blockTypeLinkTo(
                                            i,
                                            generalLink,
                                            router,
                                            jsBridge,
                                            settingStore
                                        );
                                    }}
                                >
                                    <div className='w-[100px] h-[100px] rounded-[4px] object-cover'>
                                        <ImgLazy
                                            classNames='rounded-[4px]'
                                            width={100}
                                            height={100}
                                            src={i?.heroImage?.src ?? ''}
                                        />
                                    </div>

                                    {i.shareIcon?.boolValue && (
                                        <img
                                            className='w-[20px] absolute top-[8px] right-[8px] z-10'
                                            src={withBasePath('/images/share_blue.svg')}
                                            alt=''
                                            loading='lazy'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const shareLink =
                                                    i.blockType?.value === 'Internal'
                                                        ? i.internalLink?.value ?? ''
                                                        : `${content?.key}/${i.key}/`;

                                                jsBridge.appShareUrl(
                                                    `${isShareUrl(
                                                        i.blockType?.value,
                                                        shareLink,
                                                        i.raceLink?.targetItem?.path
                                                    )}`,
                                                    i
                                                );
                                            }}
                                        />
                                    )}

                                    {isTimeFrame(
                                        (i.isIconDisplay && i.isIconDisplay.boolValue) || false,
                                        i.iconDisplayStartDate?.dateValue || 0,
                                        i.iconDisplayEndDate?.dateValue || 0
                                    ) && (
                                        <img
                                            src={i.iconDisplay?.targetItem?.iconImage?.src}
                                            alt=''
                                            loading='lazy'
                                            className='h-[20px] absolute top-[12px] left-[12px]'
                                        ></img>
                                    )}
                                    <div className='pl-[10px] flex flex-col justify-start break-all'>
                                        <div
                                            className='text-[#031E6C] text-[13px] mb-[2px] categoryBr min-h-[16.5px] line-clamp-1'
                                            dangerouslySetInnerHTML={{
                                                __html: categoryText,
                                            }}
                                        ></div>
                                        <div
                                            className='text-[15px] line-clamp-4 leading-[1.27] min-h-[78px] align-bottom'
                                            style={{ wordBreak: 'break-word' }}
                                            dangerouslySetInnerHTML={{
                                                __html: i.pageTitle?.value || '',
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </CategorySwiper>
        </div>
    );
}
